import { ClientEvents } from 'common/events/ClientEvents';
import { useEffect, useState } from 'react';
import { CELEBRITY_EVENT_HAS_ORDER_LIMIT } from 'src/celebrity-event/Content';
import { HasOrderedBeforeDocument, HasOrderedBeforeQuery, OrderShefFragment } from 'src/gqlReactTypings.generated.d';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useGqlClient } from 'src/shared/useGqlClient';

interface IProps {
  shef: OrderShefFragment;
}

// Note: This is safe to use in parallel, since Apollo client will recognize duplicate GQL requests and only send one
export const useHasReachedCelebrityOrderLimit = ({ shef }: IProps) => {
  const [hasReachedCelebrityOrderLimit, setHasReachedCelebrityOrderLimit] = useState<boolean>(false);
  const [currentUser] = useCurrentUser();
  const GqlClient = useGqlClient();
  const tracker = useTracker();

  useEffect(() => {
    let isMounted = true;
    const shefId = shef.id;
    const userId = currentUser?.id;

    if (shef.celebrityEventType && userId && CELEBRITY_EVENT_HAS_ORDER_LIMIT[shef.celebrityEventType]) {
      GqlClient.query<HasOrderedBeforeQuery>({
        query: HasOrderedBeforeDocument,
        variables: {
          shefId,
          userId,
        },
        fetchPolicy: 'cache-first',
      })
        .then((consumerOrderResults) => {
          const hasOrderedBefore = consumerOrderResults.data?.orders?.count > 0;
          if (isMounted) setHasReachedCelebrityOrderLimit(hasOrderedBefore);
        })
        .catch((error) => {
          tracker.track(ClientEvents.CELEBRITY_EVENT_ERROR, { errors: [error.message] });
          if (isMounted) setHasReachedCelebrityOrderLimit(false);
        });
    }

    // For handling unmounts before api call is complete
    return () => {
      isMounted = false;
    };
  }, [shef.id, shef.celebrityEventType, currentUser?.id, GqlClient, tracker]);

  return [hasReachedCelebrityOrderLimit];
};
